package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.core.tween
import androidx.compose.foundation.pager.PagerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.navigation.NavController
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.QuizComboFive
import com.shadowings.gero.domain.model.QuizThreeErrors
import com.shadowings.gero.util.SoundPlayer
import com.shadowings.gero.util.rememberHapticVibration
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * Incapsula tutto lo stato e la logica comune a ogni pagina di quiz:
 * - stato UI (pending, showResult, correct, blinking)
 * - aggiornamento combo / errori
 * - inserimento di QuizComboFive / QuizThreeErrors nella lista
 * - navigazione alla pagina successiva o popBackStack
 *
 * Uso:
 *   val quizPageState = rememberQuizPageState(...)
 *   quizPageState.onConfirm(correct = ...)   // quando l'utente preme "Conferma"
 *   quizPageState.onContinue()               // quando l'utente preme "Continua"
 *   // oppure passa direttamente quizPageState.onBottomButtonClick a QuizBottomButton
 */
class QuizPageState(
    val quizId: String,
    private val page: Int,
    private val pagerState: PagerState,
    private val navController: NavController,
    private val quizResults: MutableState<Map<String, Boolean>>,
    private val quizList: MutableState<List<IQuizContent>>,
    private val comboCount: MutableState<Int>,
    private val errorsCount: MutableState<Int>,
    private val scope: CoroutineScope,
    private val onComboFiveReached: (() -> Unit)?,
    private val onNextQuestion: ((isCorrect: Boolean) -> Unit)?,
    private val vibrate: (durationMs: Long) -> Unit,
    private val previewMode: Boolean = false,
) {
    var pending by mutableStateOf(true)
        private set
    var showResult by mutableStateOf(previewMode)
        private set
    var correct by mutableStateOf(false)
        private set
    var blinking by mutableStateOf(false)
        private set
    var blockClicks by mutableStateOf(false)
        private set

    fun restoreState(
        pending: Boolean,
        showResult: Boolean,
        correct: Boolean,
        blinking: Boolean,
        blockClicks: Boolean
    ) {
        this.pending = pending
        this.showResult = showResult
        this.correct = correct
        this.blinking = blinking
        this.blockClicks = blockClicks
    }

    /** Chiamato quando l'utente preme "Conferma". Riceve il risultato calcolato dal composable. */
    fun onConfirm(isCorrect: Boolean) {
        correct = isCorrect
        quizResults.value += (quizId to correct)
        comboCount.value = if (correct) comboCount.value + 1 else 0
        errorsCount.value = if (correct) 0 else errorsCount.value + 1

        if (correct) {
            SoundPlayer.playCorrect()
            vibrate(50)
        } else {
            SoundPlayer.playIncorrect()
            vibrate(300)
        }

        if (comboCount.value == GameConstants.COMBO_THRESHOLD) {
            onComboFiveReached?.invoke()
            quizList.value = quizList.value.toMutableList()
                .apply { add(page + 1, QuizComboFive()) }
        }
        if (errorsCount.value == GameConstants.ERRORS_THRESHOLD) {
            quizList.value = quizList.value.toMutableList()
                .apply { add(page + 1, QuizThreeErrors()) }
        }

        pending = false
        showResult = true
        blinking = true
        blockClicks = true
        scope.launch {
            delay(100)
            blinking = false
            delay(50)
            blinking = true
            delay(50)
            blinking = false
            blockClicks = false
        }
    }

    /** Chiamato quando l'utente preme "Continua". */
    fun onContinue() {
        if (blockClicks) return
        if (page < quizList.value.lastIndex) {
            // Rimuovi un cuore SOLO se la risposta era sbagliata
            if (!correct) {
                onNextQuestion?.invoke(false)
            }
            scope.launch {
                pagerState.animateScrollToPage(
                    page = page + 1,
                    animationSpec = tween(500)
                )
            }
        } else {
            // Rimuovi un cuore SOLO se la risposta era sbagliata
            if (!correct) {
                onNextQuestion?.invoke(false)
            }
            navController.popBackStack()
        }
    }

    /**
     * Callback unificata da passare a [QuizBottomButton].
     * Il composable deve calcolare `isCorrect` e passarlo tramite [confirmAnswer].
     */
    fun onBottomButtonClick(confirmAnswer: () -> Boolean) {
        if (pending) onConfirm(confirmAnswer()) else onContinue()
    }
}

@Composable
fun rememberQuizPageState(
    quizId: String,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    quizList: MutableState<List<IQuizContent>>,
    comboCount: MutableState<Int>,
    errorsCount: MutableState<Int>,
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    previewMode: Boolean = false,
): QuizPageState {
    val scope = rememberCoroutineScope()
    val vibrate = rememberHapticVibration()

    val saver = Saver<QuizPageState, List<Boolean>>(
        save = { listOf(it.pending, it.showResult, it.correct, it.blinking, it.blockClicks) },
        restore = { list ->
            QuizPageState(
                quizId = quizId,
                page = page,
                pagerState = pagerState,
                navController = navController,
                quizResults = quizResults,
                quizList = quizList,
                comboCount = comboCount,
                errorsCount = errorsCount,
                scope = scope,
                onComboFiveReached = onComboFiveReached,
                onNextQuestion = onNextQuestion,
                vibrate = vibrate,
                previewMode = previewMode
            ).apply {
                restoreState(
                    pending = list[0],
                    showResult = list[1],
                    correct = list[2],
                    blinking = list[3],
                    blockClicks = list[4]
                )
            }
        }
    )

    return rememberSaveable(quizId, page, saver = saver) {
        QuizPageState(
            quizId = quizId,
            page = page,
            pagerState = pagerState,
            navController = navController,
            quizResults = quizResults,
            quizList = quizList,
            comboCount = comboCount,
            errorsCount = errorsCount,
            scope = scope,
            onComboFiveReached = onComboFiveReached,
            onNextQuestion = onNextQuestion,
            vibrate = vibrate,
            previewMode = previewMode
        )
    }
}
